import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FaSignOutAlt,
  FaFolder,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaPlus,
  FaGlobe,
  FaGithub,
  FaTools,
  FaFileAlt,
  FaCode,
  FaTrash,
  FaEdit,
  FaUpload,
  FaBriefcase,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaBars,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { getProjectsList } from "../data/data.js";
import { getResumeUrl } from "./Resume";
import { getSkillsList } from "./Skills";
import { getExperiencesList } from "./Experience";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSidebar, setActiveSidebar] = useState("projects");
  const [resumeLink, setResumeLink] = useState(getResumeUrl());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Toast Notification System
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "success", title = "") => {
    const id = Date.now() + Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type, title }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Skill Management States
  const [skillsList, setSkillsList] = useState(getSkillsList());
  const [newSkillName, setNewSkillName] = useState("");
  const [newSkillCategory, setNewSkillCategory] = useState("Frontend");
  const [newSkillImg, setNewSkillImg] = useState("");
  const [showAddSkillForm, setShowAddSkillForm] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState(null);

  // Project Management States
  const [projectsList, setProjectsList] = useState(getProjectsList());
  const [newProjName, setNewProjName] = useState("");
  const [newProjCategory, setNewProjCategory] = useState("Full Stack MERN");
  const [newProjIntro, setNewProjIntro] = useState("");
  const [newProjTech, setNewProjTech] = useState("");
  const [newProjGithub, setNewProjGithub] = useState("");
  const [newProjLive, setNewProjLive] = useState("");
  const [newProjImg, setNewProjImg] = useState("");
  const [showAddProjForm, setShowAddProjForm] = useState(false);
  const [editingProjId, setEditingProjId] = useState(null);

  const handleProjectImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProjImg(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditProjectClick = (project) => {
    setEditingProjId(project.id);
    setNewProjName(project.name || "");
    setNewProjCategory(project.category || "Full Stack MERN");
    setNewProjIntro(project.intro || "");
    setNewProjTech(project.technology || "");
    setNewProjGithub(project.github || "");
    setNewProjLive(project.live || "");
    setNewProjImg(project.image || "");
    setShowAddProjForm(true);
  };

  const handleAddProject = (e) => {
    e.preventDefault();
    if (!newProjName.trim()) return;

    let updated;
    if (editingProjId) {
      updated = projectsList.map((p) =>
        p.id === editingProjId
          ? {
            ...p,
            name: newProjName.trim(),
            category: newProjCategory,
            intro: newProjIntro.trim() || "Full stack web application project.",
            technology: newProjTech.trim() || "React.js, Tailwind, Node.js",
            github: newProjGithub.trim(),
            live: newProjLive.trim(),
            image: newProjImg || p.image,
          }
          : p
      );
      setEditingProjId(null);
      showToast(`Updated project "${newProjName.trim()}" successfully!`, "info", "Project Updated");
    } else {
      const newProject = {
        id: Date.now(),
        name: newProjName.trim(),
        category: newProjCategory,
        intro: newProjIntro.trim() || "Full stack web application project.",
        technology: newProjTech.trim() || "React.js, Tailwind, Node.js",
        github: newProjGithub.trim(),
        live: newProjLive.trim(),
        image: newProjImg || "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=800&q=80",
      };
      updated = [newProject, ...projectsList];
      showToast("New project added successfully to portfolio!", "success", "Project Added");
    }

    setProjectsList(updated);
    localStorage.setItem("custom_projects", JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new CustomEvent("projectsUpdated"));

    setNewProjName("");
    setNewProjIntro("");
    setNewProjTech("");
    setNewProjGithub("");
    setNewProjLive("");
    setNewProjImg("");
    setShowAddProjForm(false);
  };

  const handleDeleteProject = (projToDelete) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete the project "${projToDelete.name}"?`);
    if (!confirmDelete) return;

    const updated = projectsList.filter((p) => p.id !== projToDelete.id && p.name !== projToDelete.name);
    setProjectsList(updated);
    localStorage.setItem("custom_projects", JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new CustomEvent("projectsUpdated"));
    showToast(`Deleted project "${projToDelete.name}".`, "error", "Project Removed");
  };

  // Experience Management States
  const [experiencesList, setExperiencesList] = useState(getExperiencesList());
  const [expCompany, setExpCompany] = useState("");
  const [expRole, setExpRole] = useState("");
  const [expType, setExpType] = useState("Full Stack Training");
  const [expSide, setExpSide] = useState("right");
  const [expLocation, setExpLocation] = useState("");
  const [expDate, setExpDate] = useState("");
  const [expPointsText, setExpPointsText] = useState("");
  const [showAddExpForm, setShowAddExpForm] = useState(false);
  const [editingExpId, setEditingExpId] = useState(null);

  const handleEditExpClick = (exp) => {
    setEditingExpId(exp.id);
    setExpCompany(exp.company || "");
    setExpRole(exp.role || "");
    setExpType(exp.type || "Full Stack Training");
    setExpSide(exp.side || "right");
    setExpLocation(exp.location || "");
    setExpDate(exp.date || "");
    setExpPointsText((exp.points || []).join("\n"));
    setShowAddExpForm(true);
  };

  const handleAddExperience = (e) => {
    e.preventDefault();
    if (!expCompany.trim() || !expRole.trim()) return;

    const pointsArray = expPointsText
      .split("\n")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    let updated;
    if (editingExpId) {
      updated = experiencesList.map((e) =>
        e.id === editingExpId
          ? {
            ...e,
            company: expCompany.trim(),
            role: expRole.trim(),
            type: expType.trim() || "Full Time",
            side: expSide,
            location: expLocation.trim() || "Remote / On-site",
            date: expDate.trim() || "Present",
            points: pointsArray.length > 0 ? pointsArray : e.points,
          }
          : e
      );
      setEditingExpId(null);
      showToast(`Updated experience entry for "${expCompany.trim()}"!`, "info", "Experience Updated");
    } else {
      const newExp = {
        id: Date.now(),
        company: expCompany.trim(),
        role: expRole.trim(),
        type: expType.trim() || "Full Time",
        side: expSide,
        location: expLocation.trim() || "Remote / On-site",
        date: expDate.trim() || "Present",
        points: pointsArray.length > 0 ? pointsArray : ["Engineered applications and features."],
      };
      updated = [newExp, ...experiencesList];
      showToast("New work experience entry added to timeline!", "success", "Experience Added");
    }

    setExperiencesList(updated);
    localStorage.setItem("custom_experiences", JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));

    setExpCompany("");
    setExpRole("");
    setExpLocation("");
    setExpDate("");
    setExpPointsText("");
    setShowAddExpForm(false);
  };

  const handleDeleteExperience = (expToDelete) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete experience entry for "${expToDelete.company}"?`);
    if (!confirmDelete) return;

    const updated = experiencesList.filter((e) => e.id !== expToDelete.id && e.company !== expToDelete.company);
    setExperiencesList(updated);
    localStorage.setItem("custom_experiences", JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));
    showToast(`Deleted experience entry "${expToDelete.company}".`, "error", "Experience Removed");
  };

  const handleSaveResume = () => {
    localStorage.setItem("custom_resume_url", resumeLink);
    showToast("Resume link updated! Portfolio now points to the new URL.", "success", "Resume Saved");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewSkillImg(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSkillClick = (skill) => {
    setEditingSkillId(skill.id);
    setNewSkillName(skill.name || "");
    setNewSkillCategory(skill.category || "Frontend");
    setNewSkillImg(skill.img || "");
    setShowAddSkillForm(true);
  };

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;

    let updated;
    if (editingSkillId) {
      updated = skillsList.map((s) =>
        s.id === editingSkillId
          ? {
            ...s,
            name: newSkillName.trim(),
            category: newSkillCategory,
            img: newSkillImg || s.img,
          }
          : s
      );
      setEditingSkillId(null);
      showToast(`Updated skill "${newSkillName.trim()}"!`, "info", "Skill Updated");
    } else {
      const newSkill = {
        id: Date.now(),
        name: newSkillName.trim(),
        category: newSkillCategory,
        img: newSkillImg || "https://cdn-icons-png.flaticon.com/512/1089/1089129.png",
      };
      updated = [newSkill, ...skillsList];
      showToast("New skill added successfully to frontend!", "success", "Skill Added");
    }

    setSkillsList(updated);
    localStorage.setItem("custom_skills", JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));

    setNewSkillName("");
    setNewSkillImg("");
    setShowAddSkillForm(false);
  };

  const handleDeleteSkill = (skillToDelete) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete the skill "${skillToDelete.name}"?`);
    if (!confirmDelete) return;

    const updated = skillsList.filter((s) => s.id !== skillToDelete.id && s.name !== skillToDelete.name);
    setSkillsList(updated);
    localStorage.setItem("custom_skills", JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));
    showToast(`Deleted skill "${skillToDelete.name}".`, "error", "Skill Removed");
  };

  const userEmail = localStorage.getItem("userEmail") || "parthadebnath161@gmail.com";

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (isLoggedIn !== "true") {
      navigate("/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userEmail");
    navigate("/login");
  };

  const sidebarNavItems = [
    { id: "projects", label: "Projects", icon: <FaFolder size={18} />, count: projectsList.length },
    { id: "experience", label: "Experience", icon: <FaBriefcase size={18} />, count: experiencesList.length },
    { id: "skills", label: "Skills", icon: <FaTools size={18} />, count: skillsList.length },
    { id: "resume", label: "Resume", icon: <FaFileAlt size={18} /> },
  ];

  return (
    <div className="min-h-screen w-full bg-[#f8fafc] text-slate-900 font-sans flex flex-col md:flex-row">

      {/* MOBILE TOP BAR */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white/90 border-b border-slate-200 sticky top-0 z-50 backdrop-blur-xl">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 via-sky-600 to-cyan-600 flex items-center justify-center text-white font-bold shadow-sm">
            <FaCode size={16} />
          </div>
          <span className="text-sm font-extrabold text-slate-900">
            Partha<span className="text-blue-600">.Admin</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="p-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg shadow-sm"
            title="Live Site"
          >
            <FaGlobe className="text-blue-600" />
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-700 bg-white border border-slate-200 rounded-lg shadow-sm"
          >
            {mobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>
      </div>

      {/* MOBILE DRAWER OVERLAY */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white/95 backdrop-blur-2xl p-6 flex flex-col justify-between pt-20">
          <nav className="space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest px-2 mb-3">
              Admin Navigation
            </p>
            {sidebarNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSidebar(item.id);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl font-semibold text-sm transition-all ${activeSidebar === item.id
                    ? "bg-blue-500/15 text-blue-700 border border-blue-500/40 shadow-sm"
                    : "text-slate-700 hover:text-slate-950 bg-slate-100 border border-slate-200"
                  }`}
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {item.count !== undefined && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded-md bg-white border border-slate-200 text-slate-700">
                    {item.count}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="space-y-3 pt-6 border-t border-slate-200">
            <Link
              to="/"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl shadow-sm"
            >
              <FaGlobe className="text-blue-600" /> View Live Site
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 rounded-xl shadow-sm"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      )}

      {/* DESKTOP LEFT SIDEBAR NAVIGATION */}
      <aside className="w-64 bg-white border-r border-slate-200 backdrop-blur-xl hidden md:flex flex-col justify-between p-6 sticky top-0 h-screen z-40">
        <div className="space-y-8">

          {/* Admin Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 via-sky-600 to-cyan-600 flex items-center justify-center text-white font-bold shadow-md">
              <FaCode size={20} />
            </div>
            <div>
              <h1 className="text-base font-extrabold text-slate-900 leading-tight">
                Partha<span className="text-blue-600">.Admin</span>
              </h1>
              <p className="text-[11px] text-slate-500">Portfolio Portal</p>
            </div>
          </div>

          {/* Navigation Links List */}
          <nav className="space-y-2">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest px-3 mb-2">
              Menu Navigation
            </p>
            {sidebarNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSidebar(item.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all ${activeSidebar === item.id
                    ? "bg-blue-500/15 text-blue-700 border border-blue-500/40 shadow-sm"
                    : "text-slate-700 hover:text-slate-950 hover:bg-slate-100 border border-transparent"
                  }`}
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {item.count !== undefined && (
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700">
                    {item.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Sidebar Footer Controls */}
        <div className="space-y-3 pt-6 border-t border-slate-200">
          <Link
            to="/"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-slate-700 hover:text-slate-950 bg-slate-50 border border-slate-200 rounded-xl transition-all shadow-sm"
          >
            <FaGlobe className="text-blue-600" /> View Live Site
          </Link>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:opacity-90 rounded-xl shadow-md transition-all"
          >
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </aside>

      {/* RIGHT MAIN CONTENT AREA */}
      <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto">

        {/* Top Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-white/10">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 capitalize">
              {activeSidebar} <span className="gradient-text">Management</span>
            </h2>
            <p className="text-xs text-slate-500 mt-1">Logged in as {userEmail}</p>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
          <div className="bg-white border border-slate-200 p-6 rounded-2xl flex items-center justify-between shadow-sm">
            <div>
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Total Projects</p>
              <p className="text-3xl font-extrabold text-slate-900 mt-1">{projectsList.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-600">
              <FaFolder size={22} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-2xl flex items-center justify-between shadow-sm">
            <div>
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Skills Tracked</p>
              <p className="text-3xl font-extrabold text-slate-900 mt-1">{skillsList.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-600">
              <FaTools size={22} />
            </div>
          </div>
        </div>

        {/* PROJECTS TAB */}
        {activeSidebar === "projects" && (
          <div className="space-y-6">

            {/* Header & Add Button */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-white">Portfolio Projects ({projectsList.length})</h3>
                <p className="text-xs text-slate-400 mt-1">Manage project details, screenshots, GitHub links, and live demo URLs.</p>
              </div>

              <button
                onClick={() => setShowAddProjForm(!showAddProjForm)}
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 hover:opacity-95 rounded-xl transition-all shadow-md shadow-blue-500/20 transform hover:-translate-y-0.5"
              >
                <FaPlus /> {showAddProjForm ? "Close Form" : "Add New Project"}
              </button>
            </div>

            {/* ADD NEW PROJECT FORM */}
            {showAddProjForm && (
              <form onSubmit={handleAddProject} className="bg-slate-900/80 border border-blue-500/30 p-6 sm:p-8 rounded-2xl space-y-6 shadow-2xl">
                <h4 className="text-base font-bold text-white flex items-center gap-2">
                  <FaFolder className="text-blue-400" /> {editingProjId ? "Edit Portfolio Project" : "Add Portfolio Project"}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Project Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Project Name / Title <span className="text-emerald-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={newProjName}
                      onChange={(e) => setNewProjName(e.target.value)}
                      required
                      placeholder="e.g. StyleHive E-Commerce"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  {/* Category Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Category <span className="text-emerald-400">*</span>
                    </label>
                    <select
                      value={newProjCategory}
                      onChange={(e) => setNewProjCategory(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    >
                      <option value="Full Stack MERN">Full Stack MERN</option>
                      <option value="SaaS App">SaaS App</option>
                      <option value="Frontend & Web">Frontend & Web</option>
                    </select>
                  </div>

                  {/* GitHub Link */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      GitHub Repository URL
                    </label>
                    <input
                      type="url"
                      value={newProjGithub}
                      onChange={(e) => setNewProjGithub(e.target.value)}
                      placeholder="https://github.com/IM-Partha/..."
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  {/* Live Demo Link */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Live Demo Website URL
                    </label>
                    <input
                      type="url"
                      value={newProjLive}
                      onChange={(e) => setNewProjLive(e.target.value)}
                      placeholder="https://my-app.vercel.app"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>

                {/* Technologies List */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    Technologies Used (comma separated)
                  </label>
                  <input
                    type="text"
                    value={newProjTech}
                    onChange={(e) => setNewProjTech(e.target.value)}
                    placeholder="React.js, Tailwind CSS, Express.js, MongoDB"
                    className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                  />
                </div>

                {/* Description Intro */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    Project Description / Summary
                  </label>
                  <textarea
                    rows={3}
                    value={newProjIntro}
                    onChange={(e) => setNewProjIntro(e.target.value)}
                    placeholder="Brief description of project features and accomplishments..."
                    className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none resize-none"
                  />
                </div>

                {/* Screenshot Banner Upload */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    Upload Project Screenshot / Cover Image <span className="text-emerald-400">*</span>
                  </label>
                  <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl bg-slate-950/60 border border-dashed border-white/20">
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-xs font-bold cursor-pointer hover:bg-emerald-500/30 transition-all">
                      <FaUpload /> Choose Project Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProjectImageUpload}
                        className="hidden"
                      />
                    </label>

                    <span className="text-xs text-slate-400">
                      {newProjImg ? "Screenshot selected!" : "Upload PNG / JPG screenshot"}
                    </span>

                    {/* Image Preview Box */}
                    {newProjImg && (
                      <div className="ml-auto w-24 h-14 rounded-lg bg-slate-900 border border-emerald-400/50 p-1 overflow-hidden">
                        <img src={newProjImg} alt="Project Preview" className="w-full h-full object-cover rounded-md" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddProjForm(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white bg-slate-950 border border-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 text-white font-bold text-xs uppercase tracking-wider shadow-md shadow-blue-500/20 hover:opacity-95 transition-all"
                  >
                    Save Project to Portfolio
                  </button>
                </div>
              </form>
            )}

            {/* PROJECTS GRID LIST */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {projectsList.map((project) => (
                <div
                  key={project.id}
                  className="group relative bg-slate-900/60 border border-white/10 p-6 rounded-2xl flex flex-col justify-between hover:border-blue-500/40 transition-colors shadow-lg"
                >
                  {/* Edit & Delete Action Buttons */}
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 z-20">
                    <button
                      onClick={() => handleEditProjectClick(project)}
                      className="p-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-md transition-all active:scale-95"
                      title="Edit Project"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project)}
                      className="p-2 rounded-xl bg-red-600 hover:bg-red-500 text-white shadow-md transition-all active:scale-95"
                      title="Delete Project"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>

                  <div>
                    {/* Cover Banner Thumbnail */}
                    {project.image && (
                      <div className="w-full h-36 rounded-xl overflow-hidden mb-4 bg-slate-950 border border-white/10">
                        <img src={project.image} alt={project.name} className="w-full h-full object-cover" />
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-white text-lg">{project.name}</h4>
                      {project.category && (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-400">
                          {project.category}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-slate-200 mt-2 leading-relaxed line-clamp-2">{project.intro}</p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                    <span className="text-[11px] text-blue-400 font-medium line-clamp-1">{project.technology}</span>
                    <div className="flex gap-2">
                      {project.github && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2.5 bg-slate-950 border border-white/10 rounded-xl text-slate-300 hover:text-blue-400 transition-colors"
                          title="GitHub Code"
                        >
                          <FaGithub size={16} />
                        </a>
                      )}
                      {project.live && (
                        <a
                          href={project.live}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2.5 bg-slate-950 border border-white/10 rounded-xl text-slate-300 hover:text-blue-400 transition-colors"
                          title="Live Demo"
                        >
                          <FaExternalLinkAlt size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EXPERIENCE TAB */}
        {activeSidebar === "experience" && (
          <div className="space-y-6">

            {/* Header & Add Button */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-white">Work Experience & Timeline ({experiencesList.length})</h3>
                <p className="text-xs text-slate-400 mt-1">Manage career history, internships, roles, and key bullet point accomplishments.</p>
              </div>

              <button
                onClick={() => setShowAddExpForm(!showAddExpForm)}
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 hover:opacity-95 rounded-xl transition-all shadow-md shadow-blue-500/20 transform hover:-translate-y-0.5"
              >
                <FaPlus /> {showAddExpForm ? "Close Form" : "Add New Experience"}
              </button>
            </div>

            {/* ADD NEW EXPERIENCE FORM */}
            {showAddExpForm && (
              <form onSubmit={handleAddExperience} className="bg-slate-900/80 border border-blue-500/30 p-6 sm:p-8 rounded-2xl space-y-6 shadow-2xl">
                <h4 className="text-base font-bold text-white flex items-center gap-2">
                  <FaBriefcase className="text-blue-400" /> {editingExpId ? "Edit Work Experience" : "Add Work Experience"}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Company Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Company / Organization <span className="text-emerald-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={expCompany}
                      onChange={(e) => setExpCompany(e.target.value)}
                      required
                      placeholder="e.g. AlmaBetter, Euphoria Genx"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  {/* Role Title */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Role / Designation <span className="text-emerald-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={expRole}
                      onChange={(e) => setExpRole(e.target.value)}
                      required
                      placeholder="e.g. Web Development Trainee, MERN Developer"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  {/* Badge / Type */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Experience Badge / Type
                    </label>
                    <input
                      type="text"
                      value={expType}
                      onChange={(e) => setExpType(e.target.value)}
                      placeholder="e.g. Full Stack Training, Internship, Full-Time"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={expLocation}
                      onChange={(e) => setExpLocation(e.target.value)}
                      placeholder="e.g. Bangalore, Karnataka"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>

                {/* Duration & Timeline Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Date Duration
                    </label>
                    <input
                      type="text"
                      value={expDate}
                      onChange={(e) => setExpDate(e.target.value)}
                      placeholder="e.g. Apr 2025 – Oct 2025"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Timeline Side / Alignment
                    </label>
                    <select
                      value={expSide}
                      onChange={(e) => setExpSide(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    >
                      <option value="right">Right Side</option>
                      <option value="left">Left Side</option>
                    </select>
                  </div>
                </div>

                {/* Bullet Points */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    Key Responsibilities / Bullet Points (One per line)
                  </label>
                  <textarea
                    rows={4}
                    value={expPointsText}
                    onChange={(e) => setExpPointsText(e.target.value)}
                    placeholder="Engineered 3+ full-stack MERN applications&#10;Built 20+ reusable React.js components using Tailwind CSS&#10;Integrated REST APIs handling secure authentication"
                    className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddExpForm(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white bg-slate-950 border border-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 text-white font-bold text-xs uppercase tracking-wider shadow-md shadow-blue-500/20 hover:opacity-95 transition-all"
                  >
                    Save Experience to Portfolio
                  </button>
                </div>
              </form>
            )}

            {/* EXPERIENCE CARDS GRID */}
            <div className="space-y-4">
              {experiencesList.map((exp, idx) => (
                <div
                  key={exp.id || idx}
                  className="group relative bg-slate-900/60 border border-white/10 p-6 rounded-2xl space-y-4 hover:border-blue-500/40 transition-all shadow-lg"
                >
                  {/* Edit & Delete Action Buttons */}
                  <div className="absolute top-6 right-6 flex items-center gap-1.5 z-10 transition-all">
                    <button
                      onClick={() => handleEditExpClick(exp)}
                      className="p-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-md transition-all active:scale-95"
                      title="Edit Experience"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteExperience(exp)}
                      className="p-2 rounded-xl bg-red-600 hover:bg-red-500 text-white shadow-md transition-all active:scale-95"
                      title="Delete Experience"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>

                  <div className="flex items-start justify-between flex-wrap gap-2 pr-20">
                    <div>
                      <h4 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                        {exp.company}
                      </h4>
                      <p className="text-sm font-semibold text-blue-400 mt-0.5">{exp.role}</p>
                    </div>
                    {exp.type && (
                      <span className="text-xs px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300 font-medium">
                        {exp.type}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
                    {exp.location && (
                      <span className="flex items-center gap-1.5">
                        <FaMapMarkerAlt className="text-sky-400" /> {exp.location}
                      </span>
                    )}
                    {exp.date && (
                      <span className="flex items-center gap-1.5">
                        <FaCalendarAlt className="text-cyan-400" /> {exp.date}
                      </span>
                    )}
                  </div>

                  {exp.points && exp.points.length > 0 && (
                    <ul className="space-y-2 pt-2 border-t border-white/10 text-xs text-slate-300">
                      {exp.points.map((pt, pIdx) => (
                        <li key={pIdx} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                          <span className="leading-relaxed">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

          </div>
        )}

        {/* SKILLS TAB */}
        {activeSidebar === "skills" && (
          <div className="space-y-6">

            {/* Header & Add Button */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-white">Configured Technologies & Skills ({skillsList.length})</h3>
                <p className="text-xs text-slate-400 mt-1">Upload technology logos, titles, and categories to update the main portfolio.</p>
              </div>

              <button
                onClick={() => setShowAddSkillForm(!showAddSkillForm)}
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 hover:opacity-95 rounded-xl transition-all shadow-md shadow-blue-500/20 transform hover:-translate-y-0.5"
              >
                <FaPlus /> {showAddSkillForm ? "Close Form" : "Add New Skill"}
              </button>
            </div>

            {/* ADD NEW SKILL FORM */}
            {showAddSkillForm && (
              <form onSubmit={handleAddSkill} className="bg-slate-900/80 border border-blue-500/30 p-6 sm:p-8 rounded-2xl space-y-6 shadow-2xl">
                <h4 className="text-base font-bold text-white flex items-center gap-2">
                  <FaTools className="text-blue-400" /> {editingSkillId ? "Edit Technology / Skill" : "Add Technology / Skill"}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Skill Title / Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Skill Title / Name <span className="text-emerald-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={newSkillName}
                      onChange={(e) => setNewSkillName(e.target.value)}
                      required
                      placeholder="e.g. Next.js, Docker, Python"
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  {/* Category Dropdown */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                      Category <span className="text-emerald-400">*</span>
                    </label>
                    <select
                      value={newSkillCategory}
                      onChange={(e) => setNewSkillCategory(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white text-slate-900 font-semibold text-sm border border-emerald-400/50 focus:border-emerald-500 outline-none"
                    >
                      <option value="Frontend">Frontend</option>
                      <option value="Backend & DB">Backend & DB</option>
                      <option value="CMS & Design">CMS & Design</option>
                      <option value="Tools">Tools</option>
                    </select>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                    Upload Technology Logo / Image <span className="text-emerald-400">*</span>
                  </label>
                  <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl bg-slate-950/60 border border-dashed border-white/20">
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-xs font-bold cursor-pointer hover:bg-emerald-500/30 transition-all">
                      <FaUpload /> Choose Image File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>

                    <span className="text-xs text-slate-400">
                      {newSkillImg ? "Image selected!" : "Upload PNG / SVG / JPG logo image"}
                    </span>

                    {/* Image Preview Box */}
                    {newSkillImg && (
                      <div className="ml-auto w-12 h-12 rounded-lg bg-slate-900 border border-emerald-400/50 p-1.5 flex items-center justify-center">
                        <img src={newSkillImg} alt="Preview" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddSkillForm(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white bg-slate-950 border border-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 text-white font-bold text-xs uppercase tracking-wider shadow-md shadow-blue-500/20 hover:opacity-95 transition-all"
                  >
                    Save Skill to Portfolio
                  </button>
                </div>
              </form>
            )}

            {/* SKILLS GRID CARDS */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {skillsList.map((skill, idx) => (
                <div
                  key={skill.id || idx}
                  className="group relative bg-slate-900/60 border border-white/10 hover:border-blue-500/40 p-4 rounded-2xl flex flex-col items-center justify-between transition-all shadow-md"
                >
                  {/* Edit & Delete Action Buttons */}
                  <div className="absolute top-2.5 right-2.5 flex items-center gap-1 z-10 transition-all">
                    <button
                      onClick={() => handleEditSkillClick(skill)}
                      className="p-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white shadow-md transition-all active:scale-95"
                      title="Edit Skill"
                    >
                      <FaEdit size={12} />
                    </button>
                    <button
                      onClick={() => handleDeleteSkill(skill)}
                      className="p-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white shadow-md transition-all active:scale-95"
                      title="Delete Skill"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>

                  <div className={`w-12 h-12 flex items-center justify-center mb-2.5 p-1 rounded-xl ${skill.name.toLowerCase().includes("express") ? "bg-slate-950 border border-white/20" : ""
                    }`}>
                    {skill.img ? (
                      <img src={skill.img} alt={skill.name} className="max-w-full max-h-full object-contain filter drop-shadow-md" />
                    ) : (
                      <FaTools className="text-blue-600" size={20} />
                    )}
                  </div>

                  <span className="text-xs font-semibold text-slate-200 text-center">{skill.name}</span>
                  <span className="text-[10px] text-blue-400 font-mono mt-1 uppercase tracking-wider">{skill.category}</span>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* RESUME TAB */}
        {activeSidebar === "resume" && (
          <div className="bg-slate-900/60 border border-white/10 p-8 rounded-2xl max-w-2xl space-y-6">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaFileAlt className="text-emerald-400" /> Resume Link Settings
            </h3>

            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Google Drive Resume URL
              </label>
              <input
                type="text"
                value={resumeLink}
                onChange={(e) => setResumeLink(e.target.value)}
                placeholder="https://drive.google.com/file/d/..."
                className="w-full px-4 py-3.5 rounded-xl bg-white text-slate-900 font-semibold placeholder:text-gray-400 text-sm border border-emerald-400/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 outline-none transition-all"
              />
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <button
                onClick={handleSaveResume}
                className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 via-sky-600 to-cyan-600 text-white font-bold text-xs uppercase tracking-wider hover:opacity-95 transition-all shadow-md shadow-blue-500/20 flex items-center gap-2 transform hover:-translate-y-0.5"
              >
                <FaCheckCircle size={14} /> Save & Update Resume Link
              </button>

              <a
                href={resumeLink}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-3.5 rounded-xl bg-slate-950 border border-white/10 text-slate-300 hover:text-white font-semibold text-xs uppercase tracking-wider transition-all flex items-center gap-2"
              >
                <FaExternalLinkAlt size={14} /> Preview Link
              </a>
            </div>
          </div>
        )}

      </main>

      {/* FLOATING TOAST NOTIFICATION CONTAINER */}
      <div className="fixed top-5 right-5 z-[99999] flex flex-col gap-3 pointer-events-none max-w-sm w-full px-4 sm:px-0">
        <AnimatePresence>
          {toasts.map((toast) => {
            const isSuccess = toast.type === "success";
            const isError = toast.type === "error";
            const isInfo = toast.type === "info";

            return (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, y: -20, scale: 0.95, filter: "blur(4px)" }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -16, scale: 0.95, filter: "blur(4px)" }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className={`pointer-events-auto relative overflow-hidden rounded-2xl border p-4 shadow-2xl backdrop-blur-2xl transition-all ${isSuccess
                    ? "bg-white/95 border-emerald-200/80 shadow-emerald-500/10 text-slate-800"
                    : isError
                      ? "bg-white/95 border-rose-200/80 shadow-rose-500/10 text-slate-800"
                      : "bg-white/95 border-blue-200/80 shadow-blue-500/10 text-slate-800"
                  }`}
              >
                {/* Auto-dismiss progress line */}
                <motion.div
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: 4, ease: "linear" }}
                  className={`absolute bottom-0 left-0 h-1 w-full origin-left ${isSuccess ? "bg-emerald-500" : isError ? "bg-rose-500" : "bg-blue-500"
                    }`}
                />

                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border shadow-xs ${isSuccess
                        ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                        : isError
                          ? "bg-rose-50 border-rose-200 text-rose-600"
                          : "bg-blue-50 border-blue-200 text-blue-600"
                      }`}
                  >
                    {isSuccess && <FaCheckCircle size={16} />}
                    {isError && <FaTrash size={14} />}
                    {isInfo && <FaInfoCircle size={16} />}
                  </div>

                  <div className="flex-1 pr-2 pt-0.5">
                    {toast.title && (
                      <h5 className="text-xs font-bold text-slate-900 leading-tight">
                        {toast.title}
                      </h5>
                    )}
                    <p className="text-xs font-medium text-slate-600 mt-0.5 leading-snug">
                      {toast.message}
                    </p>
                  </div>

                  <button
                    onClick={() => removeToast(toast.id)}
                    className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
                    title="Close"
                  >
                    <FaTimes size={13} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminDashboard;

